// Ported from files/snapdraw-design-kit/code/src/lib/i18n/strings/auth.ts,
// with two corrections to keep the copy honest:
//   - "expires in 30 minutes" -> 60 minutes, matching this project's actual
//     Supabase reset-link expiry (see ForgotPasswordPage/ResetPasswordPage).
//   - paywall.included.3/4 originally dropped "pricing catalog" (at the
//     time, Catalog was a stub with no real items) and "Offline field mode"
//     (no offline support exists); paywall.expired dropped the fabricated
//     "data safe for 30 days" claim (no such retention policy existed yet).
//     Catalog shipped real Supabase-backed CRUD in G4 — included.3 updated
//     (UX-12) to match the landing page's own phrasing. LAUNCH-9's later
//     grace period + export is deliberately still not listed here — it's a
//     post-cancellation trust point, not a "what you get" subscription
//     feature, and doesn't fit this list's framing (UX-12 decision,
//     2026-09-02). Offline mode still doesn't exist.
// Also adds the real error/status-message keys the retrofitted pages need
// that the kit's static mockup copy didn't require.
// 2026-08-22 kit update added real client-side field validation
// (auth.error.required/email/password, forgot.error.required) — adopted
// verbatim, no honesty concerns with this batch.
// 2026-08-28 (backlog AUTH-1): three specific error states SignUpPage/
// LoginPage previously fell through to Supabase's raw (English-only,
// untranslatable) error text for: duplicateAccount (signing up with an
// email that already has a confirmed account — Supabase doesn't throw for
// this by default, it returns success with an empty `identities` array, a
// deliberate anti-enumeration measure), weakPassword (signup never checked
// password strength at all, unlike ResetPasswordPage's real 8-char/number/
// letter rules), and invalidCredentials (wrong password on login, mapped
// from Supabase's stable "Invalid login credentials" message).
export const en = {
  "auth.tagline": "Quotes and AIA pay applications, drawn on site and delivered before you leave it.",
  "auth.sub": "Construction · Invoicing",
  "auth.stat.quote": "Avg quote",
  "auth.stat.gc": "GC-ready",
  "auth.stat.field": "Field ready",
  "auth.copyright": "© 2026 Shaman Technologies",
  "auth.error.generic": "Something went wrong.",
  "auth.error.required": "This field is required.",
  "auth.error.email": "Enter a valid email address.",
  "auth.error.password": "Enter your password.",
  "auth.error.duplicateAccount": "An account with this email already exists.",
  "auth.error.weakPassword": "Password must be at least 8 characters, with a number and a letter.",
  "auth.error.invalidCredentials": "Incorrect email or password.",
  "login.notSureHint": "Not sure how you signed up? Try Google or Microsoft, or reset your password.",
  "auth.expired.title": "That link has expired",
  "auth.expired.body": "Links are single-use and expire after 60 minutes. If your account already confirmed, just log in below — otherwise request a fresh one.",
  "auth.expired.signupAgain": "Sign up again",
  "auth.expired.resetLink": "Send a new reset link",
  "auth.oauthError.title": "Sign-in didn't complete",
  "auth.oauthError.body": "Something interrupted sign-in with Google or Microsoft. Try again below, or log in with your email and password instead.",
  "auth.password.show": "Show password",
  "auth.password.hide": "Hide password",
  "auth.password.label": "Password",
  "login.eyebrow": "Sign in to your crew account",
  "login.email": "you@company.com",
  "login.password": "••••••••••",
  "login.keepSignedIn": "Keep me signed in",
  "login.forgot": "Forgot password?",
  "login.submit": "Log in",
  "login.newPrompt": "New to SnapDraw?",
  "login.createAccount": "Create an account",
  "sso.google": "Continue with Google",
  "sso.microsoft": "Continue with Microsoft",
  "sso.divider": "or continue with email",
  "onboarding.eyebrow": "One last thing",
  "onboarding.title": "Tell us about your business",
  "onboarding.help": "This prints at the top of every quote and pay application you send.",
  "onboarding.submit": "Continue",
  "onboarding.submitting": "Saving…",
  "onboarding.error.save": "We couldn't save your business name. Try again.",
  "signup.eyebrow": "Create your crew account",
  "signup.inviteEyebrow": "You've been invited — join the team",
  "signup.inviteHelp": "Create your password to join your teammate's SnapDraw account.",
  "signup.business": "Business name",
  "signup.havePrompt": "Already have an account?",
  "signup.login": "Log in",
  "signup.passwordPlaceholder": "Create a password",
  "signup.businessPlaceholder": "Your Business LLC",
  "signup.businessHelp": "Your business name prints on every quote and pay application.",
  "signup.submitButton": "Sign up",
  "signup.creatingAccount": "Creating account…",
  "signup.checkYourInbox": "Check your inbox",
  "signup.confirmationSentTo": "We sent a confirmation link to {email}. Click it to activate your account.",
  "signup.openEmailApp": "Open email app",
  "signup.backToLogin": "Back to log in",
  "forgot.eyebrow": "Reset your password",
  "forgot.help": "We'll email a single-use link that expires in 60 minutes.",
  "forgot.submit": "Send reset link",
  "forgot.error.required": "Password recovery requires an email.",
  "forgot.sentTitle": "Link sent",
  "forgot.useDifferent": "Use a different email",
  "forgot.remembered": "Remembered it?",
  "forgot.backToLogin": "Back to log in",
  "forgot.sentBody": "If an account exists for {email}, a reset link is on its way. Check spam before you re-send.",
  "forgot.sentFallback": "that address",
  "reset.eyebrow": "Set a new password",
  "reset.submit": "Update password",
  "reset.updating": "Updating…",
  "reset.newPasswordPlaceholder": "New password",
  "reset.confirmPasswordPlaceholder": "Confirm new password",
  "reset.linkExpired": "Link expired?",
  "reset.sendNew": "Send a new one",
  "reset.strength": "Password strength",
  "reset.rule.length": "8 characters minimum",
  "reset.rule.number": "One number",
  "reset.rule.letter": "One letter",
  "reset.rule.match": "Both entries match",
  "reset.footer": "This link is single-use and expires 60 minutes after it was sent.",
  "reset.invalidTitle": "Link invalid",
  "reset.invalidBody": "This reset link is invalid or has expired. Request a new one to continue.",
  "reset.doneTitle": "Password updated",
  "reset.doneBody": "Redirecting you to SnapDraw…",
  "reset.error.generic": "Could not update password.",
  "paywall.eyebrow": "Subscribe to keep using SnapDraw",
  "paywall.title": "Your free trial has ended",
  "paywall.expired": "{days}-day trial expired",
  "paywall.titleLapsed": "Your subscription has ended",
  "paywall.expiredLapsed": "Access ended {date}",
  "paywall.planTitle": "SnapDraw — Full access",
  "paywall.plan": "Plan",
  "paywall.included.1": "Unlimited quotes and invoices",
  "paywall.included.2": "G702 / G703 pay applications",
  "paywall.included.3": "Client directory and pricing catalog",
  "paywall.included.4": "Printable PDF quotes and pay applications",
  "paywall.subscribe": "Subscribe",
  "paywall.redirecting": "Redirecting…",
  "paywall.cancelNote": "Cancel anytime. Card charged monthly until you stop it.",
  "paywall.mobileNotice": "Visit {domain} on the web to subscribe.",
  "paywall.exportData": "Export your data",
  "paywall.exportingData": "Exporting…",
  "paywall.logout": "Log out",
  "paywall.error.generic": "Could not start checkout.",
} as const;

export const es: Record<keyof typeof en, string> = {
  "auth.tagline":
    "Cotizaciones y solicitudes de pago AIA, hechas en obra y entregadas antes de irte.",
  "auth.sub": "Construcción · Facturación",
  "auth.stat.quote": "Cotización prom.",
  "auth.stat.gc": "Listo para el GC",
  "auth.stat.field": "Listo para obra",
  "auth.copyright": "© 2026 Shaman Technologies",
  "auth.error.generic": "Algo salió mal.",
  "auth.error.required": "Este campo es obligatorio.",
  "auth.error.email": "Ingresa una dirección de correo válida.",
  "auth.error.password": "Ingresa tu contraseña.",
  "auth.error.duplicateAccount": "Ya existe una cuenta con este correo electrónico.",
  "auth.error.weakPassword": "La contraseña debe tener al menos 8 caracteres, un número y una letra.",
  "auth.error.invalidCredentials": "Correo electrónico o contraseña incorrectos.",
  "login.notSureHint": "¿No recuerdas cómo te registraste? Prueba con Google o Microsoft, o restablece tu contraseña.",
  "auth.expired.title": "Ese enlace venció",
  "auth.expired.body": "Los enlaces son de un solo uso y vencen después de 60 minutos. Si tu cuenta ya se confirmó, solo inicia sesión abajo — de lo contrario solicita uno nuevo.",
  "auth.expired.signupAgain": "Regístrate de nuevo",
  "auth.expired.resetLink": "Enviar un nuevo enlace",
  "auth.oauthError.title": "El inicio de sesión no se completó",
  "auth.oauthError.body": "Algo interrumpió el inicio de sesión con Google o Microsoft. Intenta de nuevo abajo, o inicia sesión con tu correo y contraseña.",
  "auth.password.show": "Mostrar contraseña",
  "auth.password.hide": "Ocultar contraseña",
  "auth.password.label": "Contraseña",
  "login.eyebrow": "Entra a la cuenta de tu cuadrilla",
  "login.email": "tu@empresa.com",
  "login.password": "••••••••••",
  "login.keepSignedIn": "Mantener sesión iniciada",
  "login.forgot": "¿Olvidaste tu contraseña?",
  "login.submit": "Iniciar sesión",
  "login.newPrompt": "¿Nuevo en SnapDraw?",
  "login.createAccount": "Crear una cuenta",
  "sso.google": "Continuar con Google",
  "sso.microsoft": "Continuar con Microsoft",
  "sso.divider": "o continúa con tu correo",
  "onboarding.eyebrow": "Una última cosa",
  "onboarding.title": "Cuéntanos sobre tu negocio",
  "onboarding.help": "Esto aparece en la parte superior de cada cotización y solicitud de pago que envías.",
  "onboarding.submit": "Continuar",
  "onboarding.submitting": "Guardando…",
  "onboarding.error.save": "No pudimos guardar el nombre de tu negocio. Inténtalo de nuevo.",
  "signup.eyebrow": "Crea la cuenta de tu cuadrilla",
  "signup.inviteEyebrow": "Te han invitado — únete al equipo",
  "signup.inviteHelp": "Crea tu contraseña para unirte a la cuenta de SnapDraw de tu compañero de equipo.",
  "signup.business": "Nombre del negocio",
  "signup.havePrompt": "¿Ya tienes cuenta?",
  "signup.login": "Iniciar sesión",
  "signup.passwordPlaceholder": "Crea una contraseña",
  "signup.businessPlaceholder": "Tu Empresa S.A.",
  "signup.businessHelp": "El nombre de tu negocio aparece en cada cotización y solicitud de pago.",
  "signup.submitButton": "Registrarse",
  "signup.creatingAccount": "Creando cuenta…",
  "signup.checkYourInbox": "Revisa tu bandeja de entrada",
  "signup.confirmationSentTo": "Te enviamos un enlace de confirmación a {email}. Haz clic en él para activar tu cuenta.",
  "signup.openEmailApp": "Abrir app de correo",
  "signup.backToLogin": "Volver a iniciar sesión",
  "forgot.eyebrow": "Restablece tu contraseña",
  "forgot.help": "Te enviaremos un enlace de un solo uso que vence en 60 minutos.",
  "forgot.submit": "Enviar enlace",
  "forgot.error.required": "Para recuperar la contraseña necesitamos un correo electrónico.",
  "forgot.sentTitle": "Enlace enviado",
  "forgot.useDifferent": "Usar otro correo",
  "forgot.remembered": "¿Ya la recordaste?",
  "forgot.backToLogin": "Volver al inicio de sesión",
  "forgot.sentBody": "Si existe una cuenta para {email}, un enlace de restablecimiento va en camino. Revisa spam antes de reenviar.",
  "forgot.sentFallback": "ese correo",
  "reset.eyebrow": "Crea una nueva contraseña",
  "reset.submit": "Actualizar contraseña",
  "reset.updating": "Actualizando…",
  "reset.newPasswordPlaceholder": "Nueva contraseña",
  "reset.confirmPasswordPlaceholder": "Confirma la nueva contraseña",
  "reset.linkExpired": "¿Enlace vencido?",
  "reset.sendNew": "Enviar uno nuevo",
  "reset.strength": "Seguridad de la contraseña",
  "reset.rule.length": "Mínimo 8 caracteres",
  "reset.rule.number": "Un número",
  "reset.rule.letter": "Una letra",
  "reset.rule.match": "Ambas coinciden",
  "reset.footer": "Este enlace es de un solo uso y vence 60 minutos después de enviarse.",
  "reset.invalidTitle": "Enlace inválido",
  "reset.invalidBody": "Este enlace de restablecimiento es inválido o venció. Solicita uno nuevo para continuar.",
  "reset.doneTitle": "Contraseña actualizada",
  "reset.doneBody": "Te estamos redirigiendo a SnapDraw…",
  "reset.error.generic": "No se pudo actualizar la contraseña.",
  "paywall.eyebrow": "Suscríbete para seguir usando SnapDraw",
  "paywall.title": "Tu prueba gratuita terminó",
  "paywall.expired": "Prueba de {days} días vencida",
  "paywall.titleLapsed": "Tu suscripción terminó",
  "paywall.expiredLapsed": "Tu acceso terminó el {date}",
  "paywall.planTitle": "SnapDraw — Acceso completo",
  "paywall.plan": "Plan",
  "paywall.included.1": "Cotizaciones y facturas ilimitadas",
  "paywall.included.2": "Solicitudes de pago G702 / G703",
  "paywall.included.3": "Directorio de clientes y catálogo de precios",
  "paywall.included.4": "Cotizaciones y solicitudes de pago en PDF",
  "paywall.subscribe": "Suscribirme",
  "paywall.redirecting": "Redirigiendo…",
  "paywall.cancelNote": "Cancela cuando quieras. Se cobra a tu tarjeta cada mes hasta que la detengas.",
  "paywall.mobileNotice": "Visita {domain} desde la web para suscribirte.",
  "paywall.exportData": "Exportar tus datos",
  "paywall.exportingData": "Exportando…",
  "paywall.logout": "Cerrar sesión",
  "paywall.error.generic": "No se pudo iniciar el pago.",
};
